import { Box, Grid, useMediaQuery, useTheme, Alert } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AccountingType } from 'v2-types/order';
import { TableLedgerItemColumn } from '../../services';
import useAccountingContext from '../../services/useAccountingContext';
import { ledgerColumns, parseLedgerColumns } from './services';
import useSettingTable from './table-ledger-hooks';
import VirtualizedTable from 'components/virtualized-table';
import FilterComponent from './components/filter-component';
import InvolvedPartiesDialog from './components/involved-parties-dialog';
import LedgerCards from './components/ledger-cards';

const useStyles = makeStyles(() => ({
  scrollContainer: {
    width: '100%',
    maxWidth: '100%',
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
  },
}));

type ledgerType = {
  ledgerData: AccountingType[],
}

const validateParam = (param: any) => {
  if (typeof param === 'number') {
    return param.toString();
  }
  return param as string;
};

const Ledger: FC<ledgerType> = ({ ledgerData, }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isCards = useMediaQuery(theme.breakpoints.only('xs'));
  const isScrollTable = useMediaQuery(theme.breakpoints.only('sm'));
  const { itemsCollapsed, setItemCollapse, t, setFilteredColumns, filteredColumns } = useSettingTable();
  const {
    handleAddParty,
    handleEditAmount,
    handleEditLedgerAssociation,
    handleDeleteLedgetEntry,
    handleDeleteLegerAssociation,
    handleCreateLink
  } = useAccountingContext()! || {};
  const [partiesRow, setPartiesRow] = useState<any>(null);

  const handleViewParties = useCallback((row: any) => {
    setPartiesRow(row);
  }, []);

  const columns = useMemo(() => ledgerColumns(t, handleEditAmount, handleViewParties, handleAddParty, handleDeleteLedgetEntry, handleCreateLink),
    [handleAddParty, handleDeleteLedgetEntry, handleEditAmount, handleViewParties, t, handleCreateLink]);

  const parsedcolumns = useMemo(() => parseLedgerColumns(ledgerData) as TableLedgerItemColumn[], [ledgerData]);
  const [showvoidResults, setShowVoidResults] = useState(false);
  const filterOptions = (selectedColumns: string, param: string, type: string, clear: boolean) => {
    const filteredResults = parsedcolumns.filter(f => {
      const localParam = validateParam(f[selectedColumns]);
      if (type === 'contains') {
        if (localParam && localParam.toLowerCase().includes(param.toLowerCase())) {
          return f;
        }
        return undefined;
      }
      if (type === 'equal') {
        if (localParam && localParam.toLowerCase() === param.toLowerCase()) {
          return f;
        }
        return undefined;
      }
      return undefined;
    });
    setShowVoidResults(!clear && filteredResults.length === 0);
    setFilteredColumns(() => !clear ? filteredResults : []);
  };

  useEffect(() => {
    if (filteredColumns.length > 0) {
      setFilteredColumns(prev => prev.map(f => {
        const foundTableItem = parsedcolumns.find(g => g.id === f.id);
        if (foundTableItem) {
          return foundTableItem;
        }
        return f;
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedcolumns]);

  const ledgerRows = filteredColumns.length > 0 ? filteredColumns : parsedcolumns;

  const tableElement = (
    <VirtualizedTable
      columns={columns}
      dataSet={ledgerRows}
    />
  );

  const renderContent = () => {
    if (isCards) {
      return (
        <LedgerCards
          data={ledgerRows}
          itemsCollapsed={itemsCollapsed}
          setItemCollapse={setItemCollapse}
        />
      );
    }
    if (isScrollTable) {
      return <Box className={classes.scrollContainer}>{tableElement}</Box>;
    }
    return tableElement;
  };

  return (
    <>
      <Grid container direction="column" spacing={3}>
        <Grid item xs={12}>
          <FilterComponent
            setFilter={filterOptions}
          />
        </Grid>
        {showvoidResults &&
          <Alert severity="info">
            there are no results
          </Alert>}
        <Grid item xs={12} style={{ marginTop: 16 }}>
          {renderContent()}
        </Grid>
      </Grid>
      <InvolvedPartiesDialog
        open={!!partiesRow}
        ledgerRow={partiesRow}
        onClose={() => setPartiesRow(null)}
        handleEditLedgerAssociation={handleEditLedgerAssociation}
        handleDeleteLedgerAssociation={handleDeleteLegerAssociation}
      />
    </>
  );
};

export default Ledger;
